// src/pages/ResinFormulaPage.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Beaker, 
  Leaf, 
  Shield, 
  Clock, 
  Star, 
  Download,
  PlayCircle,
  Users
} from 'lucide-react';

// ... rest of the file remains the same

const ResinFormulaPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('preservation');
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const categories = [
    {
      id: 'preservation',
      name: 'Resin Formula Preservation',
      icon: Shield,
      description: 'Learn advanced preservation techniques for long-lasting resin projects'
    },
    {
      id: 'flower',
      name: 'Resin Flower Preservation',
      icon: Leaf,
      description: 'Preserve beautiful flowers in crystal-clear resin forever'
    },
    {
      id: 'techniques',
      name: 'Advanced Techniques',
      icon: Beaker,
      description: 'Master professional resin formulation methods'
    }
  ];

  const preservationMethods = [
    {
      title: "Silica Gel Drying",
      description: "Quick and effective method for preserving flower structure",
      time: "2-5 days",
      difficulty: "Beginner",
      materials: ["Silica gel", "Airtight container", "Fresh flowers"]
    },
    {
      title: "Glycerin Treatment",
      description: "Maintains flexibility and natural feel of preserved materials",
      time: "1-2 weeks",
      difficulty: "Intermediate",
      materials: ["Glycerin", "Water", "Cutting tools", "Container"]
    },
    {
      title: "Freeze Drying",
      description: "Professional method for perfect preservation",
      time: "24-48 hours",
      difficulty: "Advanced",
      materials: ["Freeze dryer", "Vacuum chamber", "Specialized tools"]
    }
  ];

  const resinFormulas = [
    {
      name: "UV Resistant Formula",
      description: "Perfect for outdoor and high-light applications",
      components: [
        "Epoxy resin (60%)",
        "UV stabilizer (5%)",
        "Hardener (30%)",
        "Pigment (5%)"
      ],
      cureTime: "24-48 hours",
      applications: ["Outdoor furniture", "Window displays", "Art pieces"]
    },
    {
      name: "Fast-Cure Formula",
      description: "Quick setting resin for rapid project completion",
      components: [
        "Fast-cure epoxy (70%)",
        "Accelerator (10%)",
        "Hardener (20%)"
      ],
      cureTime: "4-6 hours",
      applications: ["Repairs", "Small projects", "Production work"]
    },
    {
      name: "High-Clarity Formula",
      description: "Crystal clear finish for premium projects",
      components: [
        "High-grade epoxy (65%)",
        "Clarity enhancer (5%)",
        "Hardener (30%)"
      ],
      cureTime: "48-72 hours",
      applications: ["Jewelry", "Display cases", "Optical applications"]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-dark-900 to-dark-950 relative overflow-hidden">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold font-serif text-white mb-6">
              Resin <span className="bg-gradient-to-r from-accent-400 to-accent-600 bg-clip-text text-transparent">Formulas</span>
            </h1>
            <p className="text-xl text-dark-200 max-w-3xl mx-auto leading-relaxed">
              Master the art of resin preservation with our professional formulas and techniques. 
              Create lasting beauty with scientific precision.
            </p>
          </motion.div>
        </div>
        
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-accent-600/10 rounded-full blur-3xl"></div>
      </section>

      {/* Category Navigation */}
      <section className="py-12 bg-dark-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`p-6 rounded-2xl text-left transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-br from-accent-500 to-accent-600 text-white shadow-lg scale-105'
                    : 'bg-dark-700/50 text-dark-300 hover:bg-dark-700 hover:text-white'
                }`}
                whileHover={{ scale: selectedCategory === category.id ? 1.05 : 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <category.icon className="h-12 w-12 mb-4" />
                <h3 className="text-xl font-semibold mb-2">{category.name}</h3>
                <p className="text-sm opacity-90">{category.description}</p>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            ref={ref}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={containerVariants}
          >
            {selectedCategory === 'preservation' && (
              <div className="space-y-12">
                <motion.div variants={itemVariants} className="text-center">
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Preservation Methods
                  </h2>
                  <p className="text-dark-300 max-w-2xl mx-auto">
                    Choose the right preservation method for your project requirements and timeline.
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {preservationMethods.map((method, index) => (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      className="bg-dark-800/50 backdrop-blur-md rounded-2xl p-6 border border-dark-700 hover:border-accent-500/30 transition-all duration-300"
                    >
                      <div className="mb-4">
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {method.title}
                        </h3>
                        <p className="text-dark-300 text-sm mb-4">
                          {method.description}
                        </p>
                      </div>

                      <div className="space-y-3 mb-6">
                        <div className="flex justify-between items-center">
                          <span className="text-dark-400 text-sm">Time Required:</span>
                          <span className="text-accent-400 font-medium">{method.time}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-dark-400 text-sm">Difficulty:</span>
                          <span className={`font-medium ${
                            method.difficulty === 'Beginner' ? 'text-green-400' :
                            method.difficulty === 'Intermediate' ? 'text-yellow-400' :
                            'text-red-400'
                          }`}>
                            {method.difficulty}
                          </span>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-white font-medium mb-2">Materials Needed:</h4>
                        <ul className="space-y-1">
                          {method.materials.map((material, idx) => (
                            <li key={idx} className="text-dark-300 text-sm flex items-center">
                              <div className="w-2 h-2 bg-accent-400 rounded-full mr-2"></div>
                              {material}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <button className="w-full bg-gradient-to-r from-accent-500 to-accent-600 text-white py-3 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300">
                        Learn This Method
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {selectedCategory === 'flower' && (
              <div className="space-y-12">
                <motion.div variants={itemVariants} className="text-center">
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Flower Preservation Guide
                  </h2>
                  <p className="text-dark-300 max-w-2xl mx-auto">
                    Preserve your precious flowers in crystal-clear resin to create timeless keepsakes.
                  </p>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  <motion.div variants={itemVariants} className="space-y-6">
                    <h3 className="text-2xl font-semibold text-white">Step-by-Step Process</h3>
                    
                    <div className="space-y-4">
                      {[
                        { step: 1, title: "Flower Selection", desc: "Choose fresh, perfect flowers at peak bloom" },
                        { step: 2, title: "Preparation", desc: "Clean and prepare flowers for preservation" },
                        { step: 3, title: "Drying Process", desc: "Remove moisture using your chosen method" },
                        { step: 4, title: "Resin Casting", desc: "Carefully embed flowers in resin layers" },
                        { step: 5, title: "Curing", desc: "Allow proper curing time for best results" },
                        { step: 6, title: "Finishing", desc: "Sand and polish to perfection" }
                      ].map((step, index) => (
                        <div key={index} className="flex items-start space-x-4">
                          <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {step.step}
                          </div>
                          <div>
                            <h4 className="text-white font-medium">{step.title}</h4>
                            <p className="text-dark-300 text-sm">{step.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  <motion.div variants={itemVariants} className="space-y-6">
                    <h3 className="text-2xl font-semibold text-white">Best Flowers for Preservation</h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { name: "Roses", rating: 5, note: "Classic choice, holds shape well" },
                        { name: "Pansies", rating: 5, note: "Vibrant colors, flat structure" },
                        { name: "Ferns", rating: 4, note: "Beautiful texture, delicate" },
                        { name: "Baby's Breath", rating: 4, note: "Adds texture and volume" },
                        { name: "Lavender", rating: 3, note: "Fragrant but can be challenging" },
                        { name: "Orchids", rating: 4, note: "Elegant but requires care" }
                      ].map((flower, index) => (
                        <div key={index} className="bg-dark-800/50 rounded-lg p-4">
                          <h4 className="text-white font-medium mb-1">{flower.name}</h4>
                          <div className="flex items-center mb-2">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`h-4 w-4 ${
                                  i < flower.rating ? 'text-yellow-400 fill-current' : 'text-dark-400'
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-dark-300 text-xs">{flower.note}</p>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              </div>
            )}

            {selectedCategory === 'techniques' && (
              <div className="space-y-12">
                <motion.div variants={itemVariants} className="text-center">
                  <h2 className="text-3xl font-bold text-white mb-6">
                    Professional Resin Formulas
                  </h2>
                  <p className="text-dark-300 max-w-2xl mx-auto">
                    Master these professional-grade formulas for different applications and requirements.
                  </p>
                </motion.div>

                <div className="space-y-8">
                  {resinFormulas.map((formula, index) => (
                    <motion.div
                      key={index}
                      variants={itemVariants}
                      className="bg-dark-800/50 backdrop-blur-md rounded-2xl p-8 border border-dark-700"
                    >
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                          <h3 className="text-2xl font-semibold text-white mb-4">
                            {formula.name}
                          </h3>
                          <p className="text-dark-300 mb-6">
                            {formula.description}
                          </p>
                          
                          <div className="space-y-4">
                            <div>
                              <h4 className="text-white font-medium mb-2">Components:</h4>
                              <ul className="space-y-2">
                                {formula.components.map((component, idx) => (
                                  <li key={idx} className="text-dark-300 flex items-center">
                                    <Beaker className="h-4 w-4 text-accent-400 mr-2" />
                                    {component}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div className="flex items-center space-x-4">
                              <Clock className="h-5 w-5 text-accent-400" />
                              <span className="text-white">Cure Time: {formula.cureTime}</span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-white font-medium mb-4">Best Applications:</h4>
                          <div className="grid grid-cols-1 gap-3">
                            {formula.applications.map((app, idx) => (
                              <div key={idx} className="bg-dark-700/50 rounded-lg p-3">
                                <span className="text-accent-400">{app}</span>
                              </div>
                            ))}
                          </div>
                          
                          <div className="mt-6 space-y-3">
                            <button className="w-full bg-gradient-to-r from-accent-500 to-accent-600 text-white py-3 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300 flex items-center justify-center space-x-2">
                              <Download className="h-5 w-5" />
                              <span>Download Formula</span>
                            </button>
                            
                            <button className="w-full border border-accent-500 text-accent-400 py-3 rounded-lg font-medium hover:bg-accent-500/10 transition-all duration-300 flex items-center justify-center space-x-2">
                              <PlayCircle className="h-5 w-5" />
                              <span>Watch Tutorial</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-dark-800">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-accent-500/20 to-accent-600/20 rounded-3xl p-12 text-center border border-accent-500/30"
          >
            <Users className="h-16 w-16 text-accent-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-6">
              Join Our Resin Art Community
            </h2>
            <p className="text-xl text-dark-200 mb-8 max-w-2xl mx-auto">
              Get access to exclusive formulas, techniques, and join thousands of resin artists worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-accent-500 to-accent-600 text-white py-4 px-8 rounded-lg font-medium hover:from-accent-600 hover:to-accent-700 transition-all duration-300">
                Join Community
              </button>
              <button className="border border-accent-500 text-accent-400 py-4 px-8 rounded-lg font-medium hover:bg-accent-500/10 transition-all duration-300">
                View All Formulas
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ResinFormulaPage;